/* Rippin Moto header behaviour: primary nav panels with hover intent, the
   bike chip and picker (desktop popover, phone bottom sheet), the phone bike
   bar, the phone drawer with its stacked levels, predictive search hand-off
   and the cart count. Vanilla JS, no dependencies.

   Shared contracts: localStorage `rm_bike` holds the bike handle only;
   `rm:bike-change` on document with detail {handle, label} on every pick or
   clear (other code dispatches it too, and the header re-renders on it);
   `rm:bike-picker-open` (detail {source}) and `rm:bike-open` open the picker. */
(function () {
  'use strict';

  var BIKE_KEY = 'rm_bike';
  var TAB_KEY = 'rm_bike_tab';
  var OPEN_DELAY = 150;
  var CLOSE_DELAY = 220;
  /* How long after a hover opened a panel a click on the same trigger is
     treated as part of the same gesture and left alone. */
  var HOVER_CLICK_GRACE = 400;

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function mq(query) {
    return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  }

  function canHover() {
    return mq('(hover: hover) and (pointer: fine)');
  }

  function isPhone() {
    return mq('(max-width: 749px)');
  }

  function isDesktop() {
    return mq('(min-width: 990px)');
  }

  function visible(el) {
    return !!(el && (el.offsetParent !== null || el === document.activeElement));
  }

  function cssEscape(value) {
    if (window.CSS && typeof CSS.escape === 'function') return CSS.escape(value);
    return String(value).replace(/["\\]/g, '');
  }

  function readBike() {
    try {
      return window.localStorage.getItem(BIKE_KEY) || '';
    } catch (err) {
      return '';
    }
  }

  function writeBike(handle) {
    try {
      if (handle) window.localStorage.setItem(BIKE_KEY, handle);
      else window.localStorage.removeItem(BIKE_KEY);
    } catch (err) {
      /* storage unavailable; the pick still applies to this page */
    }
  }

  function publish(name, payload) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, payload);
      }
    } catch (err) {
      /* analytics is best effort */
    }
  }

  /* The accessible name a nav link announces: its aria-label, the element it
     is labelled by, or its text. */
  function linkLabel(a) {
    var label = a.getAttribute('aria-label');
    if (label) return label;
    var by = a.getAttribute('aria-labelledby');
    var el = by ? document.getElementById(by) : null;
    return ((el || a).textContent || '').replace(/\s+/g, ' ').trim();
  }

  /* Deferred scripts run with readyState "interactive", so both paths used to
     fire and every handler was bound twice. Bind once. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  function init() {
    qsa('[data-rm-header]').forEach(function (header) {
      if (header.hasAttribute('data-rm-header-ready')) return;
      header.setAttribute('data-rm-header-ready', '');
      setupHeader(header);
    });
  }

  function setupHeader(header) {
    /* The drawer, sheet and bar are siblings of <header> (the bar is moved
       out of the section altogether), so lookups go wider than the header. */
    var scope = header.closest('.shopify-section') || document;

    /* Small hub so the nav, the picker and search can close one another:
       only one of them is open at a time. */
    var hub = {
      closeNav: function () {},
      closePicker: function () {},
      refreshNav: function () {}
    };

    var bikes = bikeApi();
    setupNav(header, hub, bikes);
    setupBikeChip(header, scope, hub, bikes);
    setupDrawer(header, scope, bikes);
    setupCart(header);
    setupPredictiveSearch(header, hub);
  }

  /* ---------------- the saved bike ----------------

     The picker buttons carry everything the header needs to show a bike:
     label, kind (emoto or gas), link and a short name for the phone chip. A
     handle that is not in the list (set by older code) still shows, by its
     handle. */
  function bikeApi() {
    function find(handle) {
      if (!handle) return null;
      return qs('[data-rm-picker] [data-bike-handle="' + cssEscape(handle) + '"]');
    }

    function current() {
      var handle = readBike();
      if (!handle) return null;
      var b = find(handle);
      var label = (b && b.getAttribute('data-bike-label')) || handle;
      return {
        handle: handle,
        label: label,
        kind: b && b.getAttribute('data-bike-kind') === 'gas' ? 'gas' : 'emoto',
        url: (b && b.getAttribute('data-bike-url')) || '/collections/rm-bike-' + handle,
        short: (b && b.getAttribute('data-bike-short')) || label,
        button: b
      };
    }

    return { find: find, current: current };
  }

  /* ---------------- cart ----------------

     Savor keeps the cart count live region and the drawer's aria-expanded sync
     inside <header-actions>, which ships with the theme header this section
     replaces. Reproduce both here. */
  function setupCart(header) {
    var trigger = qs('[data-rm-cart-trigger]', header);
    var live = qs('[data-rm-cart-live]', header);
    if (!trigger && !live) return;

    function syncExpanded() {
      if (!trigger) return;
      var drawer = document.getElementById('cart-drawer');
      if (!drawer) return;
      trigger.setAttribute('aria-expanded', drawer.hasAttribute('open') ? 'true' : 'false');
    }

    /* Count bubble. Savor updates it from <cart-icon>, which the theme header
       ships and this section does not, so the same job is done here: read the
       resolved cart from the lines-update promise, write the count into the
       bubble, and bump the icon so the add is visible without a drawer. */
    var bubble = trigger ? qs('.cart-bubble', trigger) : null;
    var bubbleCount = bubble ? qs('.cart-bubble__text-count', bubble) : null;
    var bumpTimer = null;

    function renderCount(count, animate) {
      if (live) {
        var word = count === 1 ? 'item' : 'items';
        live.textContent = 'Cart: ' + count + ' ' + word;
      }
      if (bubble && bubbleCount) {
        bubbleCount.textContent = count < 100 ? String(count) : '99+';
        bubbleCount.classList.toggle('hidden', count === 0);
        bubble.classList.toggle('visually-hidden', count === 0);
      }
      if (trigger) trigger.classList.toggle('has-items', count > 0);
      if (animate && trigger && count > 0) {
        trigger.classList.remove('is-bumping');
        void trigger.offsetWidth;
        trigger.classList.add('is-bumping');
        if (bumpTimer) window.clearTimeout(bumpTimer);
        bumpTimer = window.setTimeout(function () {
          trigger.classList.remove('is-bumping');
        }, 900);
      }
    }

    document.addEventListener('shopify:cart:lines-update', function (e) {
      var promise = e && e.promise;
      if (!promise || typeof promise.then !== 'function') return;
      promise
        .then(function (res) {
          var count = res && res.cart && res.cart.totalQuantity;
          if (count === undefined || count === null) return;
          var detail = res && res.detail;
          var added = !!(detail && detail.didError === false);
          renderCount(count, added);
        })
        .catch(function () {
          /* the cart request failed; the page shows its own error */
        });
    });

    /* Back/forward cache restores the old count; ask the cart for the real one. */
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted || !window.fetch) return;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart && typeof cart.item_count === 'number') renderCount(cart.item_count, false);
        })
        .catch(function () {});
    });

    document.addEventListener('theme-drawer:open', syncExpanded);
    document.addEventListener('theme-drawer:close', syncExpanded);

    /* The control is an <a href="/cart">. In drawer mode we cancel the
       navigation and toggle the drawer instead. When the drawer element is not
       on the page (cart_type is "page", or we are on the cart template, where
       cart-drawer.liquid renders nothing) the link is left alone and the
       browser goes to /cart. Modified clicks and non-primary buttons are always
       left to the browser so "open in a new tab" keeps working. */
    function drawerElement() {
      var drawer = document.getElementById('cart-drawer');
      if (!drawer) return null;
      if (typeof drawer.toggle !== 'function' && window.customElements) {
        try {
          window.customElements.upgrade(drawer);
        } catch (err) {
          /* nothing to upgrade; fall through to the link */
        }
      }
      return typeof drawer.toggle === 'function' ? drawer : null;
    }

    trigger &&
      trigger.addEventListener('click', function (e) {
        if (e.defaultPrevented) return;
        if (e.button && e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        var drawer = drawerElement();
        if (!drawer) return;
        e.preventDefault();
        drawer.toggle();
        window.setTimeout(syncExpanded, 0);
      });
    syncExpanded();
  }

  /* ---------------- predictive search ----------------

     One search button. With Savor's search modal on the page it opens the
     modal; on phones without the modal it opens a search field under the
     header row; otherwise the form submits to /search. The visually hidden
     input in the form is the no-JS fallback and also hands off to the modal
     if it is ever focused.

     First-letter guard (2026-09-25 audit): the old hand-off read the box when
     it was focused, then wrote that stale value over what the shopper had
     already typed into the modal, and ran twice (focus and click). Now it runs
     once, keystrokes typed while the modal is opening are buffered and
     replayed into it, the source value is read when the modal is ready, and
     focus returning to the input after the modal closes does not reopen it. */
  function setupPredictiveSearch(header, hub) {
    var openBtn = qs('[data-rm-search-open]', header);
    var input = qs('[data-rm-search-input]', header);
    var msearch = qs('[data-rm-msearch]', header);
    var msearchInput = msearch ? qs('input[type="search"]', msearch) : null;
    var msearchClose = msearch ? qs('[data-rm-msearch-close]', msearch) : null;
    var handing = false;
    var closedAt = 0;
    var buffer = '';

    function modalEl() {
      return document.getElementById('search-modal');
    }

    function modal() {
      var el = modalEl();
      return el && typeof el.showDialog === 'function' ? el : null;
    }

    function onBufferKey(e) {
      if (!handing) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key && e.key.length === 1) {
        buffer += e.key;
        e.preventDefault();
      } else if (e.key === 'Backspace' && buffer) {
        buffer = buffer.slice(0, -1);
        e.preventDefault();
      }
    }

    function finish(m, source) {
      var dialog = m.querySelector('dialog');
      var target = m.querySelector('input[name="q"]');
      var tries = 0;
      if (dialog && !dialog.hasAttribute('data-rm-close-watch')) {
        dialog.setAttribute('data-rm-close-watch', '');
        dialog.addEventListener('close', function () {
          closedAt = Date.now();
        });
      }
      (function settle() {
        if (dialog && dialog.open) {
          document.removeEventListener('keydown', onBufferKey, true);
          handing = false;
          if (target) {
            var start = (source && source.value) || '';
            var typed = start + buffer;
            buffer = '';
            if (typed) {
              target.value = typed;
              target.dispatchEvent(new Event('input', { bubbles: true }));
            }
            if (document.activeElement !== target) target.focus();
            try {
              var end = target.value.length;
              target.setSelectionRange(end, end);
            } catch (err) {
              /* search inputs in some browsers do not support selection */
            }
          }
          return;
        }
        if (++tries < 60) {
          window.setTimeout(settle, 30);
        } else {
          document.removeEventListener('keydown', onBufferKey, true);
          handing = false;
          buffer = '';
        }
      })();
    }

    /* Returns true when the modal took over (or is about to). */
    function handOff(source) {
      var el = modalEl();
      if (!el) return false;
      if (handing) return true;
      var m = modal();
      var tag = el.tagName.toLowerCase();
      if (!m && !(window.customElements && tag.indexOf('-') > 0)) return false;

      handing = true;
      buffer = '';
      document.addEventListener('keydown', onBufferKey, true);
      hub.closeNav();
      hub.closePicker(false);
      if (source && source === document.activeElement) source.blur();

      if (m) {
        m.showDialog();
        finish(m, source);
        return true;
      }

      /* The modal's script is a module that may still be loading. Wait for
         it briefly, then fall back to the plain search page. */
      var settled = false;
      customElements.whenDefined(tag).then(function () {
        if (settled) return;
        settled = true;
        var ready = modal();
        if (ready) {
          ready.showDialog();
          finish(ready, source);
        }
      });
      window.setTimeout(function () {
        if (settled) return;
        settled = true;
        document.removeEventListener('keydown', onBufferKey, true);
        handing = false;
        var form = source && source.form;
        if (form) form.submit();
        else window.location.href = '/search';
      }, 2500);
      return true;
    }

    function toggleMsearch(open) {
      if (!msearch) return;
      msearch.hidden = !open;
      if (open && msearchInput) msearchInput.focus();
    }

    if (openBtn) {
      openBtn.addEventListener('click', function (e) {
        if (handOff(input)) {
          e.preventDefault();
          return;
        }
        if (!isDesktop() && msearch) {
          e.preventDefault();
          toggleMsearch(msearch.hidden);
        }
        /* otherwise the form submits to /search */
      });
    }

    if (msearchClose) {
      msearchClose.addEventListener('click', function () {
        toggleMsearch(false);
        if (openBtn) openBtn.focus();
      });
    }

    if (input) {
      input.addEventListener('focus', function () {
        if (handing) return;
        var el = modalEl();
        var dialog = el ? el.querySelector('dialog') : null;
        if (dialog && dialog.open) return;
        if (Date.now() - closedAt < 600) return;
        handOff(input);
      });
    }
  }

  /* ---------------- primary nav: full-width panels ----------------

     Each top-level item is a real link (the label) plus a caret button that
     owns the panel through aria-controls. Shop by bike has no page of its
     own, so its label is the button. On pointer devices hovering an item
     opens its panel after OPEN_DELAY and leaving the item and the panel
     closes it after CLOSE_DELAY. A click on a label always follows the link.
     The panels hang off the bottom of the header row, which the items fill
     top to bottom, so there is no dead gap on the way down. */
  function setupNav(header, hub, bikes) {
    var groups = [];
    qsa('[data-rm-nav-item]', header).forEach(function (item) {
      var trigger = qs('[data-rm-nav-trigger]', item);
      if (!trigger) return;
      var panelId = trigger.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;
      groups.push({
        item: item,
        trigger: trigger,
        label: qs('[data-rm-nav-label]', item),
        panel: panel,
        kind: item.getAttribute('data-rm-kind') || ''
      });
    });

    var tpl = qs('template[data-rm-strip-tpl]', header);
    var openGroup = null;
    var openTimer = null;
    var closeTimer = null;
    var hoverable = canHover();

    function contains(group, node) {
      return group.item.contains(node) || group.panel.contains(node);
    }

    function focusables(group) {
      return qsa('a[href], button:not([disabled])', group.panel).filter(visible);
    }

    /* Bike strip: "Wheels for your <bike>" and so on, with an Open link to
       the bike page. Built from the template on open, since the bike is
       client state. */
    function fillStrip(panel) {
      var slot = qs('[data-rm-panel-strip]', panel);
      if (!slot) return;
      var bike = bikes.current();
      slot.textContent = '';
      if (!bike || !tpl || !tpl.content) {
        slot.hidden = true;
        return;
      }
      slot.appendChild(tpl.content.cloneNode(true));
      var text = qs('[data-rm-strip-text]', slot);
      if (text) text.textContent = (panel.getAttribute('data-rm-strip-text') || '%b').replace('%b', bike.label);
      var link = qs('[data-rm-strip-link]', slot);
      if (link) {
        link.setAttribute('href', bike.url);
        link.setAttribute('aria-label', 'Open ' + bike.label);
      }
      slot.hidden = false;
    }

    function open(group, viaHover) {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      if (openGroup === group) return;
      if (openGroup) close(openGroup);
      hub.closePicker(false);
      fillStrip(group.panel);
      group.panel.hidden = false;
      group.trigger.setAttribute('aria-expanded', 'true');
      group.item.classList.add('is-open');
      group.hoverOpenedAt = viaHover ? Date.now() : 0;
      openGroup = group;
    }

    function close(group) {
      if (!group) return;
      group.panel.hidden = true;
      group.trigger.setAttribute('aria-expanded', 'false');
      group.item.classList.remove('is-open');
      if (openGroup === group) openGroup = null;
    }

    function closeAll() {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      groups.forEach(close);
    }

    function scheduleClose() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(closeAll, CLOSE_DELAY);
    }

    hub.closeNav = closeAll;
    hub.refreshNav = function () {
      if (openGroup) fillStrip(openGroup.panel);
    };

    function topControls() {
      return qsa('.rm-nav > li > a, .rm-nav > li > button', header);
    }

    groups.forEach(function (group) {
      group.trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (openGroup !== group) {
          open(group, false);
          return;
        }
        /* The panel is usually already open because the same pointer's
           hover opened it a moment ago; closing it now would make the click
           look dead. Keyboard use never sets hoverOpenedAt. */
        if (hoverable && Date.now() - (group.hoverOpenedAt || 0) < HOVER_CLICK_GRACE) return;
        close(group);
      });

      [group.trigger, group.label].forEach(function (el) {
        if (!el) return;
        el.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            open(group, false);
            var first = focusables(group)[0];
            if (first) first.focus();
          }
        });
      });

      group.panel.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        /* Tab lists inside the panel handle their own arrows. */
        if (e.target.closest && e.target.closest('[role="tablist"]')) return;
        var list = focusables(group);
        var idx = list.indexOf(document.activeElement);
        if (idx === -1) return;
        e.preventDefault();
        var next = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
        if (next < 0) {
          group.trigger.focus();
          return;
        }
        if (next >= list.length) next = list.length - 1;
        list[next].focus();
      });

      if (hoverable) {
        group.item.addEventListener('mouseenter', function () {
          clearTimeout(closeTimer);
          clearTimeout(openTimer);
          if (openGroup === group) return;
          openTimer = setTimeout(function () {
            open(group, true);
          }, OPEN_DELAY);
        });
        group.item.addEventListener('mouseleave', function () {
          clearTimeout(openTimer);
          if (openGroup) scheduleClose();
        });
        group.panel.addEventListener('mouseenter', function () {
          clearTimeout(closeTimer);
        });
        group.panel.addEventListener('mouseleave', scheduleClose);
      }

      /* Focus leaving the item and its panel closes it, so tabbing past the
         last link behaves like Escape. */
      function onFocusOut(e) {
        var to = e.relatedTarget;
        if (!to || contains(group, to)) return;
        close(group);
      }
      group.item.addEventListener('focusout', onFocusOut);
      group.panel.addEventListener('focusout', onFocusOut);

      if (group.kind === 'bikes') setupBikePanel(group.panel);
    });

    /* Left and right walk the top-level controls (labels and carets). */
    topControls().forEach(function (el) {
      el.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        var list = topControls();
        var idx = list.indexOf(el);
        if (idx === -1) return;
        e.preventDefault();
        var next = e.key === 'ArrowRight' ? idx + 1 : idx - 1;
        if (next < 0) next = list.length - 1;
        if (next >= list.length) next = 0;
        list[next].focus();
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !openGroup) return;
      var group = openGroup;
      close(group);
      group.trigger.focus();
    });

    document.addEventListener('click', function (e) {
      if (!openGroup) return;
      if (contains(openGroup, e.target)) return;
      closeAll();
    });

    /* rm_nav_click: panel links, tiles and the bike strip. */
    groups.forEach(function (group) {
      group.panel.addEventListener('click', function (e) {
        var a = e.target.closest ? e.target.closest('a[href]') : null;
        if (!a || !group.panel.contains(a)) return;
        publish('rm_nav_click', {
          label: linkLabel(a),
          href: a.getAttribute('href'),
          panel: group.kind,
          device: 'desktop'
        });
      });
    });

    if (typeof window.matchMedia === 'function') {
      var desk = window.matchMedia('(min-width: 990px)');
      var onDesk = function () {
        if (!desk.matches) closeAll();
      };
      if (typeof desk.addEventListener === 'function') desk.addEventListener('change', onDesk);
      else if (typeof desk.addListener === 'function') desk.addListener(onDesk);
    }
  }

  /* Shop by bike panel: E-moto / Gas bikes tabs (remembered for the session
     in sessionStorage rm_bike_tab) and the gas brand list. */
  function setupBikePanel(panel) {
    var tabs = qsa('[data-rm-biketab]', panel);
    var panes = qsa('[data-rm-bikepane]', panel);

    function selectTab(kind, focus) {
      if (!tabs.length) return;
      var found = false;
      tabs.forEach(function (t) {
        if (t.getAttribute('data-rm-biketab') === kind) found = true;
      });
      if (!found) kind = 'emoto';
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-rm-biketab') === kind;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus();
      });
      panes.forEach(function (p) {
        p.hidden = p.getAttribute('data-rm-bikepane') !== kind;
      });
      try {
        window.sessionStorage.setItem(TAB_KEY, kind);
      } catch (err) {
        /* storage unavailable; the tab still switches */
      }
    }

    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () {
        selectTab(t.getAttribute('data-rm-biketab'), false);
      });
      t.addEventListener('keydown', function (e) {
        var n = null;
        if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') n = 0;
        else if (e.key === 'End') n = tabs.length - 1;
        if (n === null) return;
        e.preventDefault();
        e.stopPropagation();
        selectTab(tabs[n].getAttribute('data-rm-biketab'), true);
      });
    });

    var saved = '';
    try {
      saved = window.sessionStorage.getItem(TAB_KEY) || '';
    } catch (err) {
      saved = '';
    }
    if (saved) selectTab(saved, false);

    var brandList = qs('[data-rm-gasbrands]', panel);
    if (!brandList) return;
    var brands = qsa('[data-rm-gasbrand]', brandList);
    function selectBrand(btn, focus) {
      brands.forEach(function (b) {
        var on = b === btn;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
        var pane = document.getElementById(b.getAttribute('aria-controls'));
        if (pane) pane.hidden = !on;
      });
      if (focus) btn.focus();
    }
    brands.forEach(function (b, i) {
      b.addEventListener('click', function () {
        selectBrand(b, false);
      });
      b.addEventListener('keydown', function (e) {
        var n = null;
        if (e.key === 'ArrowDown') n = (i + 1) % brands.length;
        else if (e.key === 'ArrowUp') n = (i - 1 + brands.length) % brands.length;
        else if (e.key === 'Home') n = 0;
        else if (e.key === 'End') n = brands.length - 1;
        if (n === null) return;
        e.preventDefault();
        e.stopPropagation();
        selectBrand(brands[n], true);
      });
    });
  }

  /* ---------------- bike chip, picker, bike bar and bottom sheet ----------------

     One rendered picker serves both places. At 750px and up it lives in the
     chip's popover; below that rm-header.js moves it into the bottom sheet
     <dialog>, and a matchMedia listener moves it back, so the handlers bound
     inside it travel with it.

     Picker: E-moto / Gas bike pills, a filter field, brand rows that open to
     model buttons (one brand open at a time; the saved bike's brand opens by
     default; every matching brand opens while a filter is typed). A pick
     saves the handle, tells the page, and swaps the list for the done state;
     opening the picker with a bike already set shows the done state first. */
  function setupBikeChip(header, scope, hub, bikes) {
    var chip = qs('[data-rm-bikechip]', header);
    if (!chip) return;
    var btn = qs('[data-rm-bikechip-btn]', chip);
    var popover = qs('[data-rm-bikechip-popover]', chip);
    var picker = popover ? qs('[data-rm-picker]', popover) : null;
    if (!btn || !popover || !picker) return;

    var valueEl = qs('[data-rm-bikechip-value]', chip);
    var shortEl = qs('[data-rm-bikechip-short]', chip);
    var listView = qs('[data-rm-picker-list]', picker);
    var doneView = qs('[data-rm-picker-done]', picker);
    var doneLabel = qs('[data-rm-picker-done-label]', picker);
    var shopLink = qs('[data-rm-picker-shop]', picker);
    var input = qs('[data-rm-picker-q]', picker);
    var empty = qs('[data-rm-picker-empty]', picker);
    var emptyKind = qs('[data-rm-picker-empty-kind]', picker);
    var groupsBox = qs('[data-rm-picker-groups]', picker);
    var tabs = qsa('[data-rm-picker-tab]', picker);
    var panes = qsa('[data-rm-picker-pane]', picker);

    var sheet = qs('[data-rm-bikesheet]', scope) || qs('[data-rm-bikesheet]');
    var sheetBody = sheet ? qs('[data-rm-bikesheet-body]', sheet) : null;
    var sheetClose = sheet ? qs('[data-rm-bikesheet-close]', sheet) : null;
    var sheetOk = !!(sheet && sheetBody);
    var bar = qs('[data-rm-bikebar]');
    var phoneQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 749px)') : null;

    var tab = 'emoto';
    var expanded = null;
    var openSource = 'chip';
    var lastFocused = null;

    function useSheet() {
      return sheetOk && isPhone();
    }

    function placePicker() {
      if (!sheetOk) return;
      var target = isPhone() ? sheetBody : popover;
      if (picker.parentNode !== target) target.appendChild(picker);
    }

    function activePane() {
      for (var i = 0; i < panes.length; i++) {
        if (panes[i].getAttribute('data-rm-picker-pane') === tab) return panes[i];
      }
      return panes[0];
    }

    /* ----- render the saved bike everywhere the header shows it ----- */
    function render() {
      var bike = bikes.current();
      var label = bike ? bike.label : '';

      chip.setAttribute('data-set', bike ? 'true' : 'false');
      chip.setAttribute('data-kind', bike && bike.kind === 'gas' ? 'gas' : 'emoto');
      if (valueEl) valueEl.textContent = label || 'Select your bike';
      if (shortEl) shortEl.textContent = bike ? bike.short : 'Bike';
      /* The visible label is shortened or hidden below 1240px, so the button
         carries the full selection in its accessible name at every width. */
      btn.setAttribute('aria-label', 'Your bike: ' + (label || 'not set') + '. Choose your bike');

      if (bar) {
        bar.setAttribute('data-set', bike ? 'true' : 'false');
        var barLabel = qs('[data-rm-bikebar-label]', bar);
        if (barLabel) barLabel.textContent = label;
        var barLink = qs('[data-rm-bikebar-link]', bar);
        if (barLink && bike) barLink.setAttribute('href', bike.url);
      }

      qsa('[data-bike-handle]', picker).forEach(function (b) {
        var on = !!bike && b.getAttribute('data-bike-handle') === bike.handle;
        b.setAttribute('aria-current', on ? 'true' : 'false');
        b.classList.toggle('is-on', on);
      });

      if (doneLabel) doneLabel.textContent = label;
      if (shopLink && bike) shopLink.setAttribute('href', bike.url);
      hub.refreshNav();
    }

    /* ----- list state ----- */
    function setAcc(acc, on) {
      acc.classList.toggle('is-open', on);
      var head = qs('[data-rm-acc-head]', acc);
      if (head) head.setAttribute('aria-expanded', on ? 'true' : 'false');
    }

    function setTab(kind, focus) {
      var found = false;
      tabs.forEach(function (t) {
        if (t.getAttribute('data-rm-picker-tab') === kind) found = true;
      });
      tab = found ? kind : 'emoto';
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-rm-picker-tab') === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus();
      });
      panes.forEach(function (p) {
        p.hidden = p.getAttribute('data-rm-picker-pane') !== tab;
      });
      if (input) {
        input.placeholder = input.getAttribute(tab === 'gas' ? 'data-ph-gas' : 'data-ph-emoto') || input.placeholder;
      }
    }

    /* Every token must match the brand and model text; a four- or two-digit
       token also matches a gas year range (2019 matches 2016-2022). */
    function matches(b, tokens) {
      if (!tokens.length) return true;
      var text = b.getAttribute('data-bike-q') || '';
      var years = b.getAttribute('data-bike-years') || '';
      var range = years ? years.split('-').map(Number) : null;
      return tokens.every(function (t) {
        if (text.indexOf(t) !== -1) return true;
        if (!range) return false;
        var y = null;
        if (/^\d{4}$/.test(t)) y = Number(t);
        else if (/^\d{2}$/.test(t)) y = 2000 + Number(t);
        if (y === null) return false;
        return range.length > 1 ? y >= range[0] && y <= range[1] : y === range[0];
      });
    }

    function applyFilter() {
      var q = input ? input.value.trim().toLowerCase() : '';
      var tokens = q ? q.split(/\s+/) : [];
      var pane = activePane();
      var total = 0;
      if (!pane) return;
      qsa('[data-rm-acc]', pane).forEach(function (acc) {
        var hits = 0;
        qsa('[data-bike-handle]', acc).forEach(function (b) {
          var ok = matches(b, tokens);
          b.hidden = !ok;
          if (ok) hits++;
        });
        acc.hidden = hits === 0;
        total += hits;
        setAcc(acc, tokens.length ? hits > 0 : acc === expanded);
        /* The count follows the filter: "2 fitments" while "ktm 2019" is typed. */
        var count = qs('[data-rm-acc-count]', acc);
        if (count) {
          var n = tokens.length ? hits : Number(count.getAttribute('data-n')) || 0;
          count.textContent = n + ' ' + (n === 1 ? count.getAttribute('data-one') : count.getAttribute('data-many'));
        }
      });
      if (empty) {
        empty.hidden = total > 0;
        if (emptyKind) emptyKind.textContent = tab === 'gas' ? 'gas bikes' : 'e-moto';
      }
    }

    /* Fresh list: the saved bike's tab and brand, no filter. */
    function resetList() {
      var bike = bikes.current();
      setTab(bike && bike.kind === 'gas' ? 'gas' : 'emoto', false);
      if (input) input.value = '';
      expanded = null;
      if (bike && bike.button) {
        var acc = bike.button.closest('[data-rm-acc]');
        if (acc && activePane() && activePane().contains(acc)) expanded = acc;
      }
      applyFilter();
    }

    function showList(focusInput) {
      if (doneView) doneView.hidden = true;
      if (listView) listView.hidden = false;
      if (focusInput && input) input.focus();
    }

    function showDone(focusShop) {
      if (!doneView || !bikes.current()) {
        showList(false);
        return;
      }
      if (listView) listView.hidden = true;
      doneView.hidden = false;
      if (focusShop && shopLink) shopLink.focus();
    }

    /* ----- desktop popover ----- */
    function openPopover() {
      popover.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
    }

    function closePopover(returnFocus) {
      var wasOpen = !popover.hidden;
      popover.hidden = true;
      if (!sheetOpen()) btn.setAttribute('aria-expanded', 'false');
      if (wasOpen && returnFocus) btn.focus();
    }

    /* ----- phone bottom sheet ----- */
    function sheetOpen() {
      return !!(sheet && (sheet.open || sheet.hasAttribute('open')));
    }

    function lockScroll(on) {
      document.documentElement.classList.toggle('rm-bikesheet-lock', !!on);
    }

    function openSheet() {
      if (!sheetOk || sheetOpen()) return;
      placePicker();
      lastFocused = document.activeElement;
      if (typeof sheet.showModal === 'function') sheet.showModal();
      else sheet.setAttribute('open', '');
      btn.setAttribute('aria-expanded', 'true');
      lockScroll(true);
    }

    function closeSheet(returnFocus) {
      if (!sheetOk || !sheetOpen()) return;
      if (typeof sheet.close === 'function' && sheet.open) sheet.close();
      else sheet.removeAttribute('open');
      btn.setAttribute('aria-expanded', 'false');
      lockScroll(false);
      var back = lastFocused;
      lastFocused = null;
      if (returnFocus) {
        if (back && typeof back.focus === 'function' && document.contains(back)) back.focus();
        else btn.focus();
      }
    }

    function isOpen() {
      return !popover.hidden || sheetOpen();
    }

    /* Open whichever picker fits the viewport. */
    function openPicker(source) {
      openSource = source || 'chip';
      hub.closeNav();
      resetList();
      var set = !!bikes.current();
      if (set) showDone(false);
      else showList(false);
      if (useSheet()) {
        closePopover(false);
        openSheet();
      } else {
        closeSheet(false);
        openPopover();
        if (set) showDone(true);
        else showList(true);
      }
    }

    function closePicker(returnFocus) {
      closePopover(returnFocus);
      closeSheet(returnFocus);
    }

    hub.closePicker = closePicker;

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (isOpen()) closePicker(false);
      else openPicker('chip');
    });

    if (bar) {
      qsa('[data-rm-bikebar-choose], [data-rm-bikebar-change]', bar).forEach(function (b) {
        b.addEventListener('click', function (e) {
          e.preventDefault();
          openPicker('bar');
        });
      });
    }

    /* ----- inside the picker ----- */
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () {
        setTab(t.getAttribute('data-rm-picker-tab'), false);
        expanded = null;
        if (input) input.value = '';
        applyFilter();
      });
      t.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        var n = (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        setTab(tabs[n].getAttribute('data-rm-picker-tab'), true);
        expanded = null;
        if (input) input.value = '';
        applyFilter();
      });
    });

    if (input) input.addEventListener('input', applyFilter);

    picker.addEventListener('click', function (e) {
      var head = e.target.closest ? e.target.closest('[data-rm-acc-head]') : null;
      if (head && picker.contains(head)) {
        var acc = head.closest('[data-rm-acc]');
        var willOpen = !acc.classList.contains('is-open');
        qsa('[data-rm-acc]', acc.parentNode).forEach(function (other) {
          setAcc(other, false);
        });
        setAcc(acc, willOpen);
        expanded = willOpen ? acc : null;
        if (willOpen && typeof acc.scrollIntoView === 'function') acc.scrollIntoView({ block: 'nearest' });
        return;
      }

      var pick = e.target.closest ? e.target.closest('[data-bike-handle]') : null;
      if (pick && picker.contains(pick)) {
        var handle = pick.getAttribute('data-bike-handle');
        var label = pick.getAttribute('data-bike-label') || handle;
        writeBike(handle);
        render();
        document.dispatchEvent(new CustomEvent('rm:bike-change', { detail: { handle: handle, label: label } }));
        publish('rm_bike_change', { bike: handle, source: openSource });
        if (input) input.value = '';
        showDone(true);
      }
    });

    var keep = qs('[data-rm-picker-keep]', picker);
    if (keep) {
      keep.addEventListener('click', function () {
        closePicker(true);
      });
    }

    var change = qs('[data-rm-picker-change]', picker);
    if (change) {
      change.addEventListener('click', function () {
        resetList();
        showList(true);
      });
    }

    var clearBtn = qs('[data-rm-bikechip-clear]', picker);
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        writeBike('');
        render();
        document.dispatchEvent(new CustomEvent('rm:bike-change', { detail: { handle: '', label: '' } }));
        resetList();
        showList(true);
      });
    }

    /* Up and down walk the visible brand rows and model buttons. */
    picker.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (!groupsBox || listView.hidden) return;
      var list = qsa('[data-rm-acc-head], [data-bike-handle]', activePane()).filter(visible);
      if (!list.length) return;
      var idx = list.indexOf(document.activeElement);
      if (idx === -1 && document.activeElement !== input) return;
      e.preventDefault();
      var next = idx === -1 ? 0 : idx + (e.key === 'ArrowDown' ? 1 : -1);
      if (next < 0) {
        if (input) input.focus();
        return;
      }
      if (next >= list.length) next = list.length - 1;
      list[next].focus();
    });

    if (sheetOk) {
      if (sheetClose) {
        sheetClose.addEventListener('click', function () {
          closeSheet(true);
        });
      }
      /* Backdrop tap: the dialog itself is the target, the panel is not. */
      sheet.addEventListener('click', function (e) {
        if (e.target === sheet) closeSheet(true);
      });
      sheet.addEventListener('cancel', function (e) {
        e.preventDefault();
        closeSheet(true);
      });
      /* Links inside the sheet (Shop parts, ask us) leave the page or jump;
         close the sheet so it is not left over the page. */
      sheet.addEventListener('click', function (e) {
        var a = e.target.closest ? e.target.closest('a[href]') : null;
        if (a && sheet.contains(a)) closeSheet(false);
      });
      if (phoneQuery) {
        var onChange = function () {
          if (!isPhone()) closeSheet(false);
          else closePopover(false);
          placePicker();
        };
        if (typeof phoneQuery.addEventListener === 'function') phoneQuery.addEventListener('change', onChange);
        else if (typeof phoneQuery.addListener === 'function') phoneQuery.addListener(onChange);
      }
    }

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      /* A modal dialog normally turns Escape into cancel, but some browsers
         deliver only the keydown, so close the sheet here too. */
      if (sheetOk && sheetOpen()) {
        closeSheet(true);
        return;
      }
      if (!popover.hidden) closePopover(true);
    });

    document.addEventListener('click', function (e) {
      if (popover.hidden) return;
      if (!chip.contains(e.target)) closePopover(false);
    });

    /* Tabbing out of the popover closes it. A focusout with no destination
       is a tap or click, not a Tab (Safari never focuses a tapped button), so
       it is left to the outside-click handler. */
    chip.addEventListener('focusout', function (e) {
      if (popover.hidden) return;
      var to = e.relatedTarget;
      if (!to || chip.contains(to)) return;
      closePopover(false);
    });

    /* Anything on the page can ask for the picker: the product fitment note
       dispatches rm:bike-open, the collection page rm:bike-picker-open with
       detail {source}. Opened on the next tick: the click that asked is
       still bubbling, and the outside-click handler would shut it again. */
    function onOpenRequest(fallbackSource) {
      return function (e) {
        var source = (e && e.detail && e.detail.source) || fallbackSource;
        if (!useSheet() && typeof btn.scrollIntoView === 'function') btn.scrollIntoView({ block: 'nearest' });
        setTimeout(function () {
          openPicker(source);
        }, 0);
      };
    }
    document.addEventListener('rm:bike-open', onOpenRequest('sheet'));
    document.addEventListener('rm:bike-picker-open', onOpenRequest('collection'));

    /* Other code (the homepage guide, the builder, bike pages) sets the bike
       too and dispatches rm:bike-change; re-render on it. */
    document.addEventListener('rm:bike-change', render);

    placePicker();
    resetList();
    render();
  }

  /* ---------------- phone drawer ----------------

     A left-side <dialog>. Every level is rendered in the Liquid; this shows
     one at a time. A row with data-rm-level-go opens that level; the back
     arrow returns to the level before, with focus back on the row that
     opened it. The root shows the logo and the contact footer. */
  function setupDrawer(header, scope, bikes) {
    var burger = qs('[data-rm-burger]', header);
    var drawer = qs('[data-rm-drawer]', scope) || qs('[data-rm-drawer]');
    if (!burger || !drawer) return;

    var closeBtn = qs('[data-rm-drawer-close]', drawer);
    var backBtn = qs('[data-rm-drawer-back]', drawer);
    var logo = qs('[data-rm-drawer-logo]', drawer);
    var title = qs('[data-rm-drawer-title]', drawer);
    var body = qs('[data-rm-drawer-body]', drawer);
    var foot = qs('[data-rm-drawer-foot]', drawer);
    var mine = qs('[data-rm-drawer-mine]', drawer);
    var levels = qsa('[data-rm-level]', drawer);
    var stack = ['root'];
    var openers = [];
    var lastFocused = null;

    function level(key) {
      for (var i = 0; i < levels.length; i++) {
        if (levels[i].getAttribute('data-rm-level') === key) return levels[i];
      }
      return null;
    }

    function focusableIn(el) {
      return qsa('a[href], button:not([disabled]), input:not([disabled])', el).filter(visible);
    }

    function show(key) {
      var lv = level(key) || level('root');
      var isRoot = lv.getAttribute('data-rm-level') === 'root';
      levels.forEach(function (l) {
        l.hidden = l !== lv;
      });
      if (backBtn) backBtn.hidden = isRoot;
      if (logo) logo.hidden = !isRoot;
      if (title) {
        title.hidden = isRoot;
        title.textContent = isRoot ? '' : lv.getAttribute('data-rm-level-title') || '';
      }
      if (foot) foot.hidden = !isRoot;
      if (body) body.scrollTop = 0;
      return lv;
    }

    function renderMine() {
      if (!mine) return;
      var bike = bikes.current();
      mine.hidden = !bike;
      if (!bike) return;
      var link = qs('[data-rm-drawer-mine-link]', mine);
      var label = qs('[data-rm-drawer-mine-label]', mine);
      if (link) link.setAttribute('href', bike.url);
      if (label) label.textContent = bike.label;
    }

    function openDrawer() {
      lastFocused = document.activeElement;
      stack = ['root'];
      openers = [];
      renderMine();
      var lv = show('root');
      if (typeof drawer.showModal === 'function') drawer.showModal();
      else drawer.setAttribute('open', '');
      burger.setAttribute('aria-expanded', 'true');
      var first = focusableIn(lv)[0];
      if (first) first.focus();
    }

    function closeDrawer(returnFocus) {
      if (typeof drawer.close === 'function' && drawer.open) drawer.close();
      else drawer.removeAttribute('open');
      burger.setAttribute('aria-expanded', 'false');
      if (returnFocus !== false && lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    burger.addEventListener('click', openDrawer);
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        closeDrawer(true);
      });
    }

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        if (stack.length < 2) return;
        stack.pop();
        var lv = show(stack[stack.length - 1]);
        var opener = openers.pop();
        if (opener && lv.contains(opener)) opener.focus();
        else {
          var first = focusableIn(lv)[0];
          if (first) first.focus();
        }
      });
    }

    drawer.addEventListener('click', function (e) {
      if (e.target === drawer) {
        closeDrawer(true);
        return;
      }
      var go = e.target.closest ? e.target.closest('[data-rm-level-go]') : null;
      if (go && drawer.contains(go)) {
        var key = go.getAttribute('data-rm-level-go');
        if (!level(key)) return;
        stack.push(key);
        openers.push(go);
        var lv = show(key);
        var first = focusableIn(lv)[0];
        if (first) first.focus();
        else if (backBtn) backBtn.focus();
        return;
      }

      var a = e.target.closest ? e.target.closest('a[href]') : null;
      if (!a || !body || !body.contains(a)) return;
      var lvEl = a.closest('[data-rm-level]');
      publish('rm_nav_click', {
        label: linkLabel(a),
        href: a.getAttribute('href'),
        panel: (lvEl && lvEl.getAttribute('data-rm-level-kind')) || 'root',
        device: 'phone'
      });
      /* Same-page links (#guide, #lead-times) would leave the drawer open
         over the page; close it. Modified clicks open a new tab instead. */
      if (!(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) closeDrawer(false);
    });

    drawer.addEventListener('cancel', function (e) {
      e.preventDefault();
      closeDrawer(true);
    });

    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var list = focusableIn(drawer);
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('rm:bike-change', renderMine);

    if (typeof window.matchMedia === 'function') {
      var desk = window.matchMedia('(min-width: 990px)');
      var onDesk = function () {
        if (desk.matches && (drawer.open || drawer.hasAttribute('open'))) closeDrawer(false);
      };
      if (typeof desk.addEventListener === 'function') desk.addEventListener('change', onDesk);
      else if (typeof desk.addListener === 'function') desk.addListener(onDesk);
    }
  }
})();
